import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail, Instagram, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, BRAND, NAV_DESCRIPTIONS } from "../mock";
import BrandMark from "./BrandMark";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      {/* Top quote strip */}
      <div className="hidden md:flex items-center justify-between px-6 lg:px-10 h-10 border-b border-white/10 bg-[#062a40]/90 text-[12px] text-slate-300">
        <p className="italic font-serif-body tracking-wide truncate pr-6">
          Multi-partisan · Viewpoint diversity · Responsible discourse
        </p>
        <div className="flex items-center gap-5 shrink-0">
          <span className="uppercase tracking-[0.18em] text-[11px] text-slate-400">Connect</span>
          <a href={`mailto:${BRAND.email}`} className="flex items-center gap-1.5 hover:text-cyan-200 transition-colors">
            <Mail className="w-3.5 h-3.5" /> Email us
          </a>
          <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-cyan-200 transition-colors">
            <Instagram className="w-3.5 h-3.5" /> IG
          </a>
        </div>
      </div>

      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "backdrop-blur-md bg-[#0a3d5e]/85 border-b border-white/10"
            : "bg-[#0a3d5e]/60 border-b border-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[78px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <BrandMark className="h-12 w-auto object-contain" />
            <span className="sr-only">{BRAND.name}</span>
          </Link>

          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            className="inline-flex items-center gap-2 rounded-full border border-cyan-300/70 px-4 py-2 text-sm text-slate-100 hover:bg-cyan-300/10 transition-colors"
          >
            <span className="hidden sm:inline">Menu</span>
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* Full-screen overlay menu */}
      <div
        className={`fixed inset-0 z-30 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        <div className="absolute inset-0 bg-[#0a3d5e]/95 backdrop-blur-md" onClick={() => setOpen(false)} />
        <div className="relative h-full flex flex-col pt-[78px] md:pt-[118px] overflow-y-auto">
          <nav className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 py-10">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {NAV_LINKS.map((l, i) => (
                <li
                  key={l.to}
                  style={{
                    transitionDelay: open ? `${80 + i * 40}ms` : "0ms",
                    transform: open ? "translateY(0)" : "translateY(18px)",
                    opacity: open ? 1 : 0,
                    transition: "transform 500ms cubic-bezier(0.22,1,0.36,1), opacity 400ms ease",
                  }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `group flex items-start justify-between gap-6 rounded-2xl border border-white/10 p-6 md:p-7 bg-[#093349]/60 hover:bg-[#145074] hover:border-cyan-300/40 transition-all ${
                        isActive ? "border-cyan-300/60 bg-[#145074]" : ""
                      }`
                    }
                  >
                    <div>
                      <p className="font-serif-display text-3xl md:text-4xl text-white">{l.label}</p>
                      <p className="mt-2 text-slate-300 text-sm md:text-base">
                        {NAV_DESCRIPTIONS[l.label] || ""}
                      </p>
                    </div>
                    <ArrowUpRight className="w-6 h-6 text-cyan-200/70 group-hover:text-cyan-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="mt-12 flex flex-wrap items-center gap-5 text-sm text-slate-300 border-t border-white/10 pt-8">
              <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-2 hover:text-cyan-200">
                <Mail className="w-4 h-4" /> {BRAND.email}
              </a>
              <span className="opacity-30">•</span>
              <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-cyan-200">
                <Instagram className="w-4 h-4" /> {BRAND.instagramHandle}
              </a>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
